if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.selectOnlyGiftOption();
        this.giftSelector?.addEventListener('change', () => {
          this.giftSelector.querySelector('.product-gift-selector__error').hidden = true;
        });
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.selectOnlyGiftOption();
        const formData = new FormData(this.form);
        const giftActive = this.giftSelector?.dataset.giftActive !== 'false';
        if (!giftActive) formData.delete('gift_variant_id');
        const selectedGift = this.giftOptions.find((option) => option.checked);
        const giftVariantId = giftActive ? formData.get('gift_variant_id') || selectedGift?.value : undefined;
        if (giftActive && (this.giftOptions.length > 0 || this.giftSelector) && !giftVariantId) {
          if (this.giftSelector) this.giftSelector.querySelector('.product-gift-selector__error').hidden = false;
          this.handleErrorMessage('Please choose one gift.');
          const firstGift = this.giftSelector?.querySelector('input[type="radio"]:not(:disabled)');
          firstGift?.closest('details[data-gift-group]')?.setAttribute('open', '');
          firstGift?.focus();
          return;
        }
        if (giftVariantId && Number(giftVariantId) === Number(formData.get('id'))) {
          this.handleErrorMessage('Please choose a gift different from the device.');
          return;
        }
        let cartPayload;
        try {
          if (giftVariantId) {
            if (!selectedGift || String(selectedGift.value) !== String(giftVariantId)) throw new Error('Invalid gift');
            cartPayload = buildGiftCartPayload(formData, giftVariantId);
          } else if (formData.getAll('items[][id]').length) {
            // Submit the device and paid add-ons as one multi-item request.
            // Mixing a top-level id with items[][id] can return only the device.
            cartPayload = buildProductCartPayload(formData);
          }
        } catch (error) {
          this.handleErrorMessage(giftVariantId ? 'Please choose a valid gift.' : window.cartStrings?.error || 'Unable to add the selected accessories.');
          return;
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        if (cartPayload) {
          if (this.cart) {
            cartPayload.sections = formData.get('sections');
            cartPayload.sections_url = formData.get('sections_url');
          }
          config.body = JSON.stringify(cartPayload);
        } else {
          delete config.headers['Content-Type'];
          config.body = formData;
        }

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description || response.message || window.cartStrings?.error);

              if (giftVariantId) {
                this.error = false;
                return;
              }

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (response.items?.length) {
              const deviceItem = response.items.find((item) => String(item.id) === String(formData.get('id')));
              response.id = deviceItem?.id || response.items[0].id;
              response.key = deviceItem?.key || response.items[0].key;
              response.addedItemKeys = giftVariantId
                ? getGiftCartItemKeys(response.items, formData.get('id'), giftVariantId)
                : [...new Set([deviceItem, ...response.items].map((item) => item?.key).filter(Boolean))];
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            if (cartPayload) this.handleErrorMessage(window.cartStrings?.error || 'Unable to add the selected items.');
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          if(this.submitButton.dataset.preLabel){
           this.submitButtonText.innerHTML = this.submitButton.dataset.preLabel;
          }else{
           this.submitButtonText.text = window.variantStrings.addToCart;
          }
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      get giftSelector() {
        const associatedInput = Array.from(document.querySelectorAll('input[name="gift_variant_id"]')).find(
          (input) => input.form === this.form
        );
        if (associatedInput) return associatedInput.closest('[data-gift-selector]');
        return Array.from(document.querySelectorAll('[data-gift-selector]')).find(
          (selector) => selector.dataset.productFormId === this.form.id
        );
      }

      get giftOptions() {
        return Array.from(document.querySelectorAll('input[name="gift_variant_id"]')).filter(
          (input) => input.form === this.form && !input.disabled &&
            input.closest?.('[data-gift-selector]')?.dataset.giftActive !== 'false'
        );
      }

      selectOnlyGiftOption() {
        this.giftSelector?.updateSchedule?.();
        const options = this.giftOptions;
        if (options.length && !options.some((option) => option.checked)) {
          options[0].checked = true;
          options[0].closest?.('details[data-gift-group]')?.setAttribute?.('open', '');
          this.giftSelector?.updateGroupChoices?.();
        }
      }
    }
  );
}
